import { ChevronRightIcon } from '@bi-digital/icons';
import { Avatar, Button, useMediaQuery } from '@bi-digital/react';
import { trackGAEvent, trackGAEventForApplication } from '@bino/utils/functions/ga';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { getSignInUrl, GO_TO_APPLICATION_URL } from '../../../api';
import { useCartContext } from '../../../context/CartContext';
import useRedirectLoading from '../../../hooks/useRedirectLoading';
import styles from './CartFooter.module.css';

const initials = (userName: string | null | undefined) =>
    userName
        ?.split(' ')
        .filter((n) => n.trim().length > 0)
        .map((n) => n.trim()[0])
        .join('')
        .toUpperCase();

export function CartFooter() {
    const { t } = useTranslation();
    const { cart, userName, isAuthorized } = useCartContext();
    const { redirectLoading: loginRedirectLoading, startRedirectLoading: startLoginRedirectLoading } =
        useRedirectLoading();
    const { redirectLoading: applicationRedirectLoading, startRedirectLoading: startapplicationRedirectLoading } =
        useRedirectLoading();

    const isSmallScreen = useMediaQuery('(width < 768px)');

    const isCartEmpty = cart.items.length === 0;
    const showUserInfo = (isCartEmpty && userName) || (userName && !isSmallScreen);

    const login = async (gaStep: 'login' | 'saveForLater') => {
        startLoginRedirectLoading();
        trackGAEvent({ event: 'login', data: { method: gaStep } });
        trackGAEventForApplication(
            gaStep,
            cart.items.map((item) => ({
                id: String(item.productId),
                name: item.name,
                campus: t(`cart:campus.${item.campus}`),
            })),
        );
        window.location.href = getSignInUrl();
    };

    const goToApplication = () => {
        startapplicationRedirectLoading();
        trackGAEventForApplication(
            'goToApplication',
            cart.items.map((item) => ({
                id: String(item.productId),
                name: item.name,
                campus: t(`cart:campus.${item.campus}`),
            })),
        );
        window.location.href = !isAuthorized ? GO_TO_APPLICATION_URL : cart.biaxCartDetailPageUrl;
    };

    return !isAuthorized ? (
        <>
            {isCartEmpty && <p className={styles.returningUserMessage}>{t('cart:returningUserMessage')}</p>}
            <div className={classNames(styles.buttonContainer, !isCartEmpty && styles.buttonContainerMultipleButtons)}>
                <Button
                    onClick={() => login(isCartEmpty ? 'login' : 'saveForLater')}
                    size={isSmallScreen ? 'medium' : 'large'}
                    variant={isCartEmpty ? 'filled' : 'outlined'}
                    disabled={loginRedirectLoading}
                    submitting={loginRedirectLoading}
                >
                    {isCartEmpty ? t('common:login') : t('cart:saveMyChoices')}
                </Button>

                {!isCartEmpty && (
                    <Button
                        onClick={goToApplication}
                        disabled={applicationRedirectLoading}
                        submitting={applicationRedirectLoading}
                        size={isSmallScreen ? 'medium' : 'large'}
                        endIcon={<ChevronRightIcon aria-hidden='true' />}
                    >
                        {t('cart:gotoapplication')}
                    </Button>
                )}
            </div>
        </>
    ) : (
        <div className={styles.buttonContainerEqualWidth}>
            {showUserInfo && (
                <div className={styles.userInfo}>
                    <Avatar size='small' className={styles.userAvatar} aria-label={userName}>
                        {initials(userName)}
                    </Avatar>

                    <p className={styles.userName}>{userName}</p>
                </div>
            )}
            {!isCartEmpty && (
                <Button
                    fullwidth={!showUserInfo}
                    onClick={goToApplication}
                    disabled={applicationRedirectLoading}
                    submitting={applicationRedirectLoading}
                    size='large'
                    endIcon={<ChevronRightIcon size='small' aria-hidden='true' />}
                >
                    {t('cart:gotoapplication')}
                </Button>
            )}
        </div>
    );
}
